import { type FC, type ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BsPlus, BsX } from 'react-icons/bs';
import { Draggable } from 'react-beautiful-dnd';

import { type RootState } from '@editor2/reducers/root';
import { CollapseState } from '@editor2/types/collapseState';
import {
	editElementsCollapseState,
	deleteElementFromPage,
	skipQuestionGenerationFeedback,
	clearQuestionGenerationData
} from '@editor2/reducers/editor/editorSlice';
import {
	elementContainer,
	headerContainer,
	indent
} from '@editor2/containers/PageElementsPage/styles';
import {
	type MCQuestionPool,
	type PageElement,
	type DoubleElements,
	ElementType
} from '@editor2/types/pageElements';
import AddElementModal from '@editor2/components/AddElementModal';
import {
	getElementHumanName,
	getElementPreview,
	isDoubleElement
} from '@editor2/services/elementService';
import ButtonTooltip from '@editor2/components/ButtonTooltip';

import ElementOptionsBlock from './ElementOptionsBlock';
import ElementNameBlock from './ElementNameBlock';
import { QUESTION_TYPES } from '@editor2/constants/questionTypes';
import IconButton from '@editor2/components/IconButton';
import QuestionFeedbackModal from '@editor2/components/QuestionFeedbackModal';

interface Props {
	courseId: number;
	pageId: string;
	element: PageElement;
	index: number;
	children: ReactNode;
}

const ElementWrapper: FC<Props> = (props) => {
	const { courseId, pageId, element, index, children } = props;

	const dispatch = useDispatch();
	const { collapseState, pages, elements, config } = useSelector(
		(state: RootState) => state.courses[courseId]
	);

	const [isAddElementModalShown, setAddElementModalShown] = useState(false);
	const [isQuestionFeedbackModalShown, setQuestionFeedbackModalShown] = useState(false);
	const [collapsed, setCollapsed] = useState(true);

	useEffect(() => {
		switch (collapseState.elements) {
			case CollapseState.ExpandAllQuestions:
				if (QUESTION_TYPES.includes(element.type)) setCollapsed(false);
				break;
			case CollapseState.ExpandAll:
				setCollapsed(false);
				break;
			case CollapseState.CollapseAll:
				setCollapsed(true);
				break;
			case CollapseState.RevealAllHTML:
				if (element.type === ElementType.HTMLText || element.type === ElementType.Carousel)
					setCollapsed(false);
				break;
			default:
				break;
		}
	}, [collapseState.elements, element.type]);

	const isGeneratingQuestions =
		element.type === ElementType.MCQuestionPool && element.isGeneratingQuestions;

	useEffect(() => {
		if (isGeneratingQuestions) setCollapsed(true);
	}, [isGeneratingQuestions]);

	const deleteDoubleElement = (element: DoubleElements) => {
		if (element.direction === 'in') {
			// find nearest out if exists and delete
			const elementsAfter = pages.entities[pageId].elements.slice(index);
			const firstNestingOut = elementsAfter.find((id) => {
				const el = elements.entities[id];
				return el.type === element.type && el.direction === 'out';
			});

			if (firstNestingOut) {
				dispatch(deleteElementFromPage({ pageId, elementId: firstNestingOut, courseId }));
			}
		}

		if (element.direction === 'out') {
			// find nearest in if exists and delete
			const elementsBefore = pages.entities[pageId].elements.slice(0, index).reverse();
			const firstNestingIn = elementsBefore.find((id) => {
				const el = elements.entities[id];
				return el.type === element.type && el.direction === 'in';
			});

			if (firstNestingIn) {
				dispatch(deleteElementFromPage({ pageId, elementId: firstNestingIn, courseId }));
			}
		}
	};

	const onDeleteElementClick = () => {
		const pageType = pages.entities[pageId].page_type;
		const elementHumanName = getElementHumanName(config, pageType, element);

		// eslint-disable-next-line no-alert
		if (window.confirm(`Are you sure to delete "${elementHumanName}" element?`)) {
			dispatch(deleteElementFromPage({ pageId, elementId: element.family_id, courseId }));

			if (isDoubleElement(element.type)) {
				deleteDoubleElement(element as DoubleElements);
			}
		}
	};

	const onCollapseElementClick = () => {
		if (element.type === ElementType.MCQuestionPool && element.isGeneratingQuestions) return;
		if (element.type === ElementType.MCQuestionPool && element.needsQuestionFeedback) {
			setQuestionFeedbackModalShown(true);
			return;
		}

		dispatch(editElementsCollapseState({ collapseState: CollapseState.Mixed, courseId }));
		setCollapsed(!collapsed);
	};

	return (
		<Draggable draggableId={element.family_id} index={index}>
			{(provided) => (
				<div ref={provided.innerRef} {...provided.draggableProps}>
					<div className={`${elementContainer} ${element.indent ? indent : ''}`}>
						<div className={headerContainer} {...provided.dragHandleProps}>
							<div className="menu">
								<ButtonTooltip message="Add new element after">
									<IconButton onClick={() => setAddElementModalShown(true)}>
										<BsPlus />
									</IconButton>
								</ButtonTooltip>

								<ButtonTooltip message="Delete element">
									<IconButton
										onClick={() => onDeleteElementClick()}
										disabled={isGeneratingQuestions}>
										<BsX />
									</IconButton>
								</ButtonTooltip>
							</div>

							<div className="headerBlock">
								<ElementNameBlock
									courseId={courseId}
									pageId={pageId}
									element={element}
									onCollapse={onCollapseElementClick}
								/>

								<ElementOptionsBlock
									courseId={courseId}
									pageId={pageId}
									element={element}
									index={index}
									collapseElements={collapsed}
									onCollapse={onCollapseElementClick}
								/>
							</div>

							{collapsed && <div className="preview">{getElementPreview(element)}</div>}
						</div>

						{!collapsed && <div className="elementBody">{children}</div>}
					</div>

					{isAddElementModalShown && (
						<AddElementModal
							pageId={pageId}
							index={index + 1}
							onHide={() => setAddElementModalShown(false)}
							isShown={isAddElementModalShown}
							courseId={courseId}
						/>
					)}
					{isQuestionFeedbackModalShown && (
						<QuestionFeedbackModal
							pageId={pages.entities[pageId].family_id}
							index={index + 1}
							onHide={() => {
								setQuestionFeedbackModalShown(false);
								dispatch(
									skipQuestionGenerationFeedback({ courseId, elementId: element.family_id })
								);
								dispatch(clearQuestionGenerationData({ courseId, elementId: element.family_id }));
							}}
							isShown={isQuestionFeedbackModalShown}
							courseId={courseId}
							element={element as MCQuestionPool}
						/>
					)}
				</div>
			)}
		</Draggable>
	);
};

export default ElementWrapper;
